import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

const VaxAccountRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    try {
      const response = await api.get('/vax-account-requests');
      setRequests(response.data);
      setError('');
    } catch (failure) {
      setError(failure.response?.data?.error || 'Unable to load registration requests.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAttachment = async request => {
    try {
      const response = await api.get(`/vax-account-requests/${request.id}/attachment`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (failure) {
      setError('Unable to open the uploaded file.');
    }
  };

  const act = async (request, action) => {
    if (action === 'approve' && !window.confirm(`Is the ${request.supplier || 'VAX'} ${request.program_label || 'account'} request for ${request.agent_name} complete? Approval emails the agent.`)) return;
    setSaving(request.id);
    try {
      await api.post(`/vax-account-requests/${request.id}/${action}`);
      await load();
    } catch (failure) {
      await load();
      setError(failure.response?.data?.error || 'Unable to update the request.');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6"><div><h1 className="text-2xl font-semibold">Registration requests</h1><p>Review supplier account registration requests from MainStreet agents.</p></div><button type="button" className="px-4 py-2 border rounded" onClick={load}>Refresh</button></div>
      {error && <p role="alert" className="p-3 mb-4 bg-red-50 text-red-800">{error}</p>}
      {loading ? <p>Loading requests…</p> : requests.length === 0 ? <p>No registration requests yet.</p> : (
        <div className="space-y-4">{requests.map(request => (
          <article key={request.id} className="p-5 border rounded-xl bg-white shadow-sm">
            <div className="flex flex-wrap justify-between gap-3"><div><h2 className="font-semibold">{request.agent_name} · {request.supplier || 'VAX'} {request.program_label || 'account'}</h2><p>{request.agent_email} · {new Date(request.created_at).toLocaleString()}</p></div><strong>{request.status === 'pending' ? 'Pending' : 'Approved'}</strong></div>
            {request.phone && <p>Phone: {request.phone}</p>}
            {(request.address_line || request.city || request.state || request.postal_code) && <p>Address: {[request.address_line, [request.city, request.state].filter(Boolean).join(', '), request.postal_code].filter(Boolean).join(' · ')}</p>}
            {request.birthdate && <p>Date of birth: {String(request.birthdate).slice(0, 10)}</p>}
            {request.attestations_json && (() => {
              try {
                const record = JSON.parse(request.attestations_json);
                return <>
                  {!!record.accepted?.length && <p>Confirmed: {record.accepted.join('; ')}</p>}
                  {(record.answers || []).map(item => <p key={item.question}>{item.question} <strong>{item.answer}</strong></p>)}
                </>;
              } catch { return null; }
            })()}
            {request.has_attachment ? <p><button type="button" className="underline" onClick={() => openAttachment(request)}>View uploaded file{request.attachment_name ? ` (${request.attachment_name})` : ''}</button></p> : null}
            {request.vax_username && <p>VAX username: {request.vax_username}</p>}
            {request.message && <p className="whitespace-pre-wrap">{request.message}</p>}
            {request.status === 'pending' ? <button type="button" className="mt-3 px-4 py-2 rounded bg-blue-800 text-white disabled:opacity-50" disabled={saving === request.id} onClick={() => act(request, 'approve')}>Approve and notify agent</button> : !request.agent_notified_at ? <button type="button" className="mt-3 px-4 py-2 rounded border" disabled={saving === request.id || (request.agent_notification_claimed_at && Date.now() - new Date(request.agent_notification_claimed_at).getTime() < 300000)} onClick={() => act(request, 'notify')}>Retry agent email</button> : <p className="text-green-700">Agent email queued</p>}
          </article>
        ))}</div>
      )}
    </div>
  );
};

export default VaxAccountRequests;
